/**
 * Locale-aware date and number formatting bound to the admin's
 * Localization settings. Consumers should prefer `useFormat()` so live
 * preference changes re-render the value immediately.
 */
import { useMemo } from 'react';
import { useAdminSettings } from '@/src/modules/store/admin-settings.context';

export function formatDate(
  input: Date | string | number | null | undefined,
  format: string,
  timezone?: string,
): string {
  if (input == null) return '';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '';

  const opts: Intl.DateTimeFormatOptions = { timeZone: timezone || undefined };
  switch (format) {
    case 'DD/MM/YYYY':
      return new Intl.DateTimeFormat('en-GB', {
        ...opts,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(d);
    case 'MM/DD/YYYY':
      return new Intl.DateTimeFormat('en-US', {
        ...opts,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(d);
    case 'YYYY-MM-DD': {
      const parts = new Intl.DateTimeFormat('en-CA', {
        ...opts,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(d);
      const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
      return `${get('year')}-${get('month')}-${get('day')}`;
    }
    case 'DD MMM YYYY':
    default:
      return new Intl.DateTimeFormat('en-GB', {
        ...opts,
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(d);
  }
}

export function formatTime(
  input: Date | string | number | null | undefined,
  timeFormat: '12h' | '24h',
  timezone?: string,
): string {
  if (input == null) return '';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: timeFormat === '12h',
    timeZone: timezone || undefined,
  }).format(d);
}

export function formatNumber(value: number, numberFormat: string): string {
  if (!Number.isFinite(value)) return '';
  // Older persisted settings may carry an empty or otherwise invalid BCP-47
  // tag (the field used to be free-text). Intl throws on those, so fall back
  // to the runtime default rather than crashing the whole page.
  try {
    return new Intl.NumberFormat(numberFormat || undefined).format(value);
  } catch {
    return new Intl.NumberFormat().format(value);
  }
}

export function useFormat() {
  const { settings } = useAdminSettings();
  const l = settings.localization;
  return useMemo(
    () => ({
      date: (input: Date | string | number | null | undefined) =>
        formatDate(input, l.dateFormat, l.timezone),
      time: (input: Date | string | number | null | undefined) =>
        formatTime(input, l.timeFormat, l.timezone),
      dateTime: (input: Date | string | number | null | undefined) => {
        const d = formatDate(input, l.dateFormat, l.timezone);
        const t = formatTime(input, l.timeFormat, l.timezone);
        return d && t ? `${d} · ${t}` : d || t;
      },
      number: (value: number) => formatNumber(value, l.numberFormat),
    }),
    [l],
  );
}
